/**
 * Javascript file for the personal settings page.
 * @author Adrian Rebmann, Steffen Schuhmann, Michael Frey
 */

import {
  subscribeToNews,
  changePassword,
  deleteUser,
  getNewsletter
} from "../../../api";
import { required, sameAs, email, minLength } from "vuelidate/lib/validators";

export default {
  name: "Settings",

  /**
   * The vue instance's data object.
   * @property {boolean} active - The "delete account" view is active.
   * @property {boolean} saved - Changes were saved.
   * @property {boolean} changepass - The "change password" view is active.
   * @property {boolean} changeuser - The "change user" view is active.
   * @property {boolean} settings.letter - Users choice of newsletter subscription
   * @property {*} settings.notification - The notification type the user is to receive.
   * @property {string} mail - The user's mail.
   * @property {boolean} error - There has been an error.
   * @property {string} form.password - The new password.
   * @property {string} form.repassword - The retype of the new password.
   * @property {boolean} loggedIn - Status of the user's login. 
   * @property {boolean} sending - Information is currently being processed.
   */
  data() {
    return {
      active: false,
      changepass: false,
      settings: {
        letter: false
      },
      error: false,
      form: {
        password: "",
        repassword: ""
      },
      loggedIn: false,
      sending: false
    };
  },

  /**
   * Validation to be done to the data.
   * @property {*} form.password - validation to be done to the new password.
   * @property {*} form.repassword - validation to be done to the old password.
   */
  validations: {
    form: {
      password: {
        required,
        minLength: minLength(8)
      },
      repassword: {
        required,
        sameAsPassword: sameAs("password")
      }
    }
  },

  /**
   * Checks if the user has subscribed to the newsletter.
   * Called after the instance of settings has been mounted.
   */
  mounted() {
    getNewsletter().then(res => {
      //console.log("news: " + res);
      this.settings.letter = res;
    });
  },

  /**
   * The vue instance's method declaration.
   */
  methods: {
    /**
     * The user confirmed the deletion of the account.
     */
    onConfirm() {
      deleteUser().then(() => {
        //If this was confirmed, log the user out. 
        this.$router.push({ name: "Logout" });
      });
    },

    /**
     * The user cancelled the deletion of the account. 
     */
    onCancel() { },

    /**
     * The user clicked on the button to go back to the previously visited page.
     */
    goBack() {
      this.$router.go(-1);
    },

    /**
     * Determines if the field is invalid or not.
     * @param {string} fieldName - The name of the field.
     */
    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName];
      if (field) {
        return {
          "md-invalid": field.$invalid && field.$dirty
        };
      }
    },

    /**
     * Resets the form.
     */
    clearForm() {
      this.$v.$reset();
      //new password
      this.form.password = null;
      //old password
      this.form.repassword = null;
    },

    /**
     * Saves changes made to the user.
     */
    saveUser() {
      this.changeuser = false;
    },

    /**
     * Saves changes made to the password.
     */
    savePass() {
      changePassword(this.form.password)
        .catch(err => {
          this.changepass = false;
          console.error(err);
        });
    },

    /**
     * Validates the new password. 
     * If it is valid, it saves it.
     */
    validatePass() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.savePass();
      }
    },

    /**
     * Updates the newsletter settings. 
     */
    saveSettings() {
      subscribeToNews(this.settings.letter)
        .then(res => {
          // update profile data
          getNewsletter(res => {
            this.settings.letter = res.hasNewsletter;
          });
        })
        .catch(err => {
          console.error(err);
        });
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/app/settings/settings.js